import { Component } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { VonPageResponseModel } from '@von-development-studio/angular-rest-service';
import { LazyLoadEvent } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { BaseComponent } from 'src/app/core/base/base.component';
import { CommonTurnViewModel } from 'src/app/model/common/turn.view';
import { KhuyanaMenuItemModel } from 'src/app/model/core/khuyana-menu-item.model';
import { SecurityUserLoginModel } from 'src/app/model/security/security-user.login';
import { TURN } from '../turn.constants';
import { TurnService } from './turn.main.service';

@Component({
  selector: 'ms-common-turn',
  templateUrl: './turn.main.component.html',
})
export class TurnComponent extends BaseComponent {
  readonly screen = TURN;
  protected setScreen = true;
  turns: CommonTurnViewModel[] = [];
  selectedTurn: CommonTurnViewModel = {};
  userInformation: SecurityUserLoginModel = {};
  isCounter = false;
  isVital = false;
  isDoctor = false;
  isDefault = false;
  options: KhuyanaMenuItemModel[] = [];
  dtPatientFilter = '';
  dtDoctorFilter = '';
  dtSpecialtyFilter = '';
  isOldTurns = false;
  oldTurns: VonPageResponseModel<CommonTurnViewModel> =
    {} as VonPageResponseModel<CommonTurnViewModel>;
  dtPatientFilter2 = '';
  dtDoctorFilter2 = '';
  dtSpecialtyFilter2 = '';
  showHistoryPdf = false;
  historyFile?: SafeResourceUrl;
  showRecipePdf = false;
  recipeFile?: SafeResourceUrl;

  constructor(
    protected service: TurnService,
    protected dialogService: DialogService
  ) {
    super();
  }

  openModal = this.service.openNewTurnDialog;

  goToConsult = this.service.goToConsult;

  onInit = () => {
    this.service.setLoadingState();
    this.service.cleanConsultPatientStore();
    this.subscriptions.push(
      this.service.getTurnStore().subscribe((turns) => (this.turns = turns)),
      this.service
        .getOldTurnStore()
        .subscribe((result) => (this.oldTurns = result)),
      this.service.readRecipeStore().subscribe((result) => {
        this.recipeFile = undefined;
        this.showRecipePdf = false;
        if (result) {
          this.recipeFile = result.recipeFile;
          this.showRecipePdf = result.showRecipePdf;
        }
      }),
      this.service.getUserInformation().subscribe((userInfo) => {
        this.userInformation = userInfo;
        this.setDefaultContextMenu();
      })
    );
  };

  lazyReadOldTurns = ($event: LazyLoadEvent) => {
    this.service.setLoadingState();
    const page = $event.first! / $event.rows!;
    this.subscriptions.push(
      this.service
        .readOldTurns(
          page,
          $event.rows!,
          $event.filters!.patient,
          $event.filters!.doctor,
          $event.filters!.specialty,
          this.userInformation.username
        )
        .subscribe()
    );
  };

  seeHistory = (historyNumber: string, turnId: number, specialty: string) => {
    this.service.setLoadingState();
    this.service
      .generateHistory(historyNumber, turnId, specialty)
      .subscribe((resource) => {
        this.historyFile = resource;
        this.showHistoryPdf = true;
      });
  };

  onContextMenuSelection = () => {
    if (this.isDoctor) {
      const optionIdx = this.options.findIndex(
        (item) => item.id === '0_consult'
      );
      this.options[optionIdx].visible =
        this.selectedTurn.turnStatusCodeAlt?.toLowerCase() === 'waiting' ||
        this.selectedTurn.turnStatusCodeAlt?.toLowerCase() === 'in-progress';
    }
    if (this.isVital) {
      const optionIdx = this.options.findIndex(
        (item) => item.id === 'vitalSign'
      );
      this.options[optionIdx].visible =
        this.selectedTurn.turnStatusCodeAlt?.toLowerCase() === 'vital';
    }
  };

  private setDefaultContextMenu = () => {
    if (this.userInformation.roles) {
      if (this.userInformation.roles.isCounter && !this.isCounter) {
        if (!(this.userInformation.roles.isDoctor ?? false)) {
          this.options.push({
            id: 'updateDoctor',
            label: this.screen.actions.updateDoctor,
            styleClass: 'text-warning',
            icon: 'fas fa-pencil-alt',
            command: ($event: any) => {
              const turn = this.selectedTurn ? this.selectedTurn : $event;
              this.service.openPatchDoctorDialog(turn.id, turn.specialty, {
                id: this.selectedTurn.patient,
                fullName: this.selectedTurn.patientName,
                document: this.selectedTurn.patientDocument,
              });
            },
          });
        }

        this.options.push({
          id: 'updateStatus',
          label: this.screen.actions.updateStatus,
          styleClass: 'text-warning',
          icon: 'fas fa-pencil-alt',
          command: ($event: any) => {
            const turn = this.selectedTurn ? this.selectedTurn : $event;
            this.service.openPatchStatusDialog(turn.id, turn.specialtyCodeAlt, {
              id: this.selectedTurn.patient,
              fullName: this.selectedTurn.patientName,
              document: this.selectedTurn.patientDocument,
            });
          },
        });
        this.isCounter = true;
      }

      if (this.userInformation.roles.isVitalSign && !this.isVital) {
        this.options.push({
          id: 'vitalSign',
          label: this.screen.actions.vitalSign,
          styleClass: 'text-warning',
          icon: 'fas fa-notes-medical',
          command: ($event: any) => {
            const turn = this.selectedTurn ? this.selectedTurn : $event;
            this.goToConsult(turn, true);
          },
        });
        this.isVital = true;
      }

      if (this.userInformation.roles.isDoctor && !this.isDoctor) {
        this.options.push({
          id: '0_consult',
          label: this.screen.actions.care,
          styleClass: 'text-success',
          icon: 'fas fa-notes-medical',
          command: ($event: any) => {
            const turn: CommonTurnViewModel = this.selectedTurn
              ? this.selectedTurn
              : $event;
            this.goToConsult(turn);
          },
        });
        this.isDoctor = true;
      }

      if (!this.isDefault) {
        this.options.push({
          id: 'addComment',
          label: this.screen.actions.addComment,
          styleClass: 'text-info',
          icon: 'fas fa-align-justify',
          command: ($event: any) => {
            const turn = this.selectedTurn ? this.selectedTurn : $event;
            this.service.openCommentDialog(turn);
          },
        });
        this.isDefault = true;
      }
    }
  };
}
